/**
 * Vault Service - Unified Data Aggregation Layer
 * 
 * This is the SINGLE SOURCE OF TRUTH for all user content in Swirl.
 * 
 * PRIMARY TABLE: deepsearch.items
 * - ALL new content is written here via vaultWriterService
 * - This is the canonical storage location
 * 
 * SECONDARY TABLES (Read for backward compatibility only):
 * - public.instagram_content (legacy)
 * - public.notes (legacy)
 * - public.uploads (legacy)
 * 
 * Every feature that needs user items MUST use this service:
 * - Smart Insights
 * - Memory Surfacing
 * - Weekly Summary
 * - Momentum
 * - Focus creation
 * - Recently Saved
 * - Coming Back to You
 * - AI predictions
 * - Search
 * - Collections
 * 
 * DO NOT query content tables directly anywhere else in the codebase.
 * DO NOT write to tables directly - use vaultWriterService ONLY.
 */

import { supabase } from '../lib/supabase';

// Cache configuration
const CACHE_KEY_PREFIX = 'vault_items';
const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes (shorter for vault data)

/**
 * Standardized item schema
 * All items from any source are normalized to this structure
 */
const EMPTY_ITEM = {
  id: null,
  userId: null,
  createdAt: null,
  updatedAt: null,
  title: '',
  description: '',
  textContent: '',
  url: '',
  imageUrl: '',
  thumbnailUrl: '',
  images: [],
  tags: [],
  autoTags: [],
  meta: {},
  extractedThemes: [],
  microTopics: [],
  sourceType: '',
  channel: '',
  aiAnalysis: null,
  analysisData: null,
  embedding: null,
  originalSourceTable: '',
  originalSourceId: null,
};

/**
 * Normalize deepsearch.items to standard schema
 */
function normalizeDeepsearchItem(item) {
  return {
    id: item.id,
    userId: item.user_id,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
    title: item.title || '',
    description: item.description || '',
    textContent: item.raw_text || item.extracted_text || '',
    url: item.url || '',
    imageUrl: item.image_url || item.url || '',
    thumbnailUrl: item.thumbnail_url || item.image_url || item.url || '',
    images: item.images || [],
    tags: item.tags || [],
    autoTags: item.auto_tags || [],
    meta: item.meta || {},
    extractedThemes: item.meta?.ai_analysis?.themes || [],
    microTopics: item.auto_tags?.topics || [],
    sourceType: item.source_type || 'unknown',
    channel: item.channel || 'Upload',
    aiAnalysis: item.meta?.ai_analysis || null,
    analysisData: item.analysis_data || null,
    embedding: item.embedding || null,
    originalSourceTable: 'deepsearch.items',
    originalSourceId: item.id,
  };
}

/**
 * Normalize instagram_content to standard schema
 */
function normalizeInstagramItem(item) {
  const aiAnalysis = item.ai_analysis || item.analysis_data || {};
  const tags = aiAnalysis.tags || aiAnalysis.main_topics || aiAnalysis.topics || [];
  
  return {
    id: item.id,
    userId: item.user_id,
    createdAt: item.created_at,
    updatedAt: item.updated_at || item.created_at,
    title: item.title || '',
    description: item.description || '',
    textContent: item.transcription || item.description || '',
    url: item.url || '',
    imageUrl: item.thumbnail_url || item.url || '',
    thumbnailUrl: item.thumbnail_url || item.url || '',
    images: item.thumbnail_url ? [item.thumbnail_url] : [],
    tags: Array.isArray(tags) ? tags : [],
    autoTags: { topics: Array.isArray(tags) ? tags : [] },
    meta: { source: 'instagram', channel: item.channel },
    extractedThemes: aiAnalysis.themes || [],
    microTopics: Array.isArray(tags) ? tags : [],
    sourceType: item.content_type || 'instagram',
    channel: item.channel || 'Instagram',
    aiAnalysis: item.ai_analysis || null,
    analysisData: item.analysis_data || null,
    embedding: null,
    originalSourceTable: 'instagram_content',
    originalSourceId: item.id,
  };
}

/**
 * Normalize notes to standard schema
 */
function normalizeNoteItem(item) {
  return {
    id: item.id,
    userId: item.user_id,
    createdAt: item.created_at,
    updatedAt: item.updated_at || item.created_at,
    title: item.title || 'Note',
    description: item.content?.substring(0, 200) || '',
    textContent: item.content || '',
    url: '',
    imageUrl: '',
    thumbnailUrl: '',
    images: [],
    tags: item.tags || [],
    autoTags: { topics: item.tags || [] },
    meta: { source: 'notes' },
    extractedThemes: [],
    microTopics: item.tags || [],
    sourceType: 'note',
    channel: 'Notes',
    aiAnalysis: null,
    analysisData: null,
    embedding: null,
    originalSourceTable: 'notes',
    originalSourceId: item.id,
  };
}

/**
 * Normalize uploads to standard schema
 */
function normalizeUploadItem(item) {
  return {
    id: item.id,
    userId: item.user_id,
    createdAt: item.created_at,
    updatedAt: item.updated_at || item.created_at,
    title: item.filename || item.title || 'Upload',
    description: item.description || '',
    textContent: item.extracted_text || '',
    url: item.url || item.file_url || '',
    imageUrl: item.url || item.file_url || '',
    thumbnailUrl: item.thumbnail_url || item.url || '',
    images: item.url ? [item.url] : [],
    tags: item.tags || [],
    autoTags: { topics: item.tags || [] },
    meta: { source: 'uploads', fileType: item.file_type },
    extractedThemes: [],
    microTopics: item.tags || [],
    sourceType: item.file_type || 'upload',
    channel: 'Uploads',
    aiAnalysis: item.ai_analysis || null,
    analysisData: null,
    embedding: null,
    originalSourceTable: 'uploads',
    originalSourceId: item.id,
  };
}

/**
 * Deduplicate items by URL or ID
 */
function deduplicateItems(items) {
  const seen = new Map();
  const result = [];
  
  for (const item of items) {
    // Create a unique key based on URL (if exists) or ID
    const key = item.url && item.url.length > 10 
      ? item.url.toLowerCase().trim()
      : `${item.originalSourceTable}:${item.id}`;
    
    if (!seen.has(key)) {
      seen.set(key, true);
      result.push(item);
    } else {
      // If duplicate, prefer the one with more data
      const existingIndex = result.findIndex(r => {
        const existingKey = r.url && r.url.length > 10 
          ? r.url.toLowerCase().trim()
          : `${r.originalSourceTable}:${r.id}`;
        return existingKey === key;
      });
      
      if (existingIndex >= 0) {
        const existing = result[existingIndex];
        // Keep the one with more content
        const existingScore = (existing.title?.length || 0) + (existing.description?.length || 0);
        const newScore = (item.title?.length || 0) + (item.description?.length || 0);
        if (newScore > existingScore) {
          result[existingIndex] = item;
        }
      }
    }
  }
  
  return result;
}

/**
 * Get cached vault data
 */
function getCachedVault(userId) {
  try {
    const cached = localStorage.getItem(`${CACHE_KEY_PREFIX}_${userId}`);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const age = Date.now() - timestamp;

    if (age > CACHE_EXPIRY_MS) {
      localStorage.removeItem(`${CACHE_KEY_PREFIX}_${userId}`);
      return null;
    }

    return data;
  } catch (error) {
    console.error('[Vault] Cache read error:', error);
    return null;
  }
}

/**
 * Cache vault data
 */
function setCachedVault(userId, data) {
  try {
    const cacheData = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(`${CACHE_KEY_PREFIX}_${userId}`, JSON.stringify(cacheData));
  } catch (error) {
    console.error('[Vault] Cache write error:', error);
  }
}

/**
 * Clear vault cache for a user
 */
export function clearVaultCache(userId) {
  localStorage.removeItem(`${CACHE_KEY_PREFIX}_${userId}`);
  if (import.meta.env.DEV) console.log('[Vault] Cache cleared for user:', userId?.slice(0, 8));
}

/**
 * MAIN FUNCTION: Get all items from the unified vault
 * 
 * This fetches from ALL content sources, normalizes them,
 * deduplicates, and returns a single unified array.
 * 
 * @param {string} userId - User ID
 * @param {Object} options - Optional filters
 * @param {boolean} options.forceRefresh - Skip cache
 * @param {number} options.limit - Max items to return
 * @param {number} options.daysBack - Only items from last N days
 * @param {string[]} options.sourceTypes - Filter by source types
 * @returns {Promise<Array>} Unified array of normalized items
 */
export async function getAllItems(userId, options = {}) {
  const { forceRefresh = false, limit = null, daysBack = null, sourceTypes = null } = options;
  
  if (!userId) {
    if (import.meta.env.DEV) console.warn('[Vault] getAllItems called without userId');
    return [];
  }

  // Check cache first
  if (!forceRefresh) {
    const cached = getCachedVault(userId);
    if (cached) {
      if (import.meta.env.DEV) console.log('[Vault] Using cached data:', cached.length, 'items');
      return applyFilters(cached, { limit, daysBack, sourceTypes });
    }
  }

  if (import.meta.env.DEV) console.log('[Vault] Fetching from all sources...');
  const allItems = [];
  const sources = {
    deepsearch: 0,
    instagram: 0,
    notes: 0,
    uploads: 0,
  };

  // Calculate date cutoff if specified
  const cutoffDate = daysBack 
    ? new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000).toISOString()
    : null;

  // 1. Fetch from deepsearch.items
  try {
    let query = supabase
      .schema('deepsearch')
      .from('items')
      .select('*')
      .eq('user_id', userId)
      .not('url', 'like', 'pending://%')
      .order('created_at', { ascending: false });
    
    if (cutoffDate) {
      query = query.gte('created_at', cutoffDate);
    }

    const { data, error } = await query;

    if (error) {
      if (!error.message?.includes('does not exist') && !error.message?.includes('schema cache')) {
        if (import.meta.env.DEV) console.warn('[Vault] deepsearch.items error:', error.message);
      }
    } else if (data && data.length > 0) {
      const normalized = data.map(normalizeDeepsearchItem);
      allItems.push(...normalized);
      sources.deepsearch = data.length;
    }
  } catch (error) {
    if (import.meta.env.DEV) console.warn('[Vault] Error fetching deepsearch.items:', error);
  }

  // 2. Fetch from instagram_content
  try {
    let query = supabase
      .schema('public')
      .from('instagram_content')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (cutoffDate) {
      query = query.gte('created_at', cutoffDate);
    }

    const { data, error } = await query;

    if (error) {
      if (!error.message?.includes('does not exist')) {
        if (import.meta.env.DEV) console.warn('[Vault] instagram_content error:', error.message);
      }
    } else if (data && data.length > 0) {
      const normalized = data.map(normalizeInstagramItem);
      allItems.push(...normalized);
      sources.instagram = data.length;
    }
  } catch (error) {
    if (import.meta.env.DEV) console.warn('[Vault] Error fetching instagram_content:', error);
  }

  // 3. Fetch from notes (if exists)
  try {
    let query = supabase
      .schema('public')
      .from('notes')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (cutoffDate) {
      query = query.gte('created_at', cutoffDate);
    }

    const { data, error } = await query;

    if (error) {
      // Silently ignore if table doesn't exist
      if (!error.message?.includes('does not exist') && error.code !== 'PGRST116') {
        if (import.meta.env.DEV) console.warn('[Vault] notes error:', error.message);
      }
    } else if (data && data.length > 0) {
      const normalized = data.map(normalizeNoteItem);
      allItems.push(...normalized);
      sources.notes = data.length;
    }
  } catch (error) {
    // Silently ignore - table may not exist
  }

  // 4. Fetch from uploads (if exists)
  try {
    let query = supabase
      .schema('public')
      .from('uploads')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (cutoffDate) {
      query = query.gte('created_at', cutoffDate);
    }

    const { data, error } = await query;

    if (error) {
      // Silently ignore if table doesn't exist
      if (!error.message?.includes('does not exist') && error.code !== 'PGRST116') {
        if (import.meta.env.DEV) console.warn('[Vault] uploads error:', error.message);
      }
    } else if (data && data.length > 0) {
      const normalized = data.map(normalizeUploadItem);
      allItems.push(...normalized);
      sources.uploads = data.length;
    }
  } catch (error) {
    // Silently ignore - table may not exist
  }

  // Sort all items by createdAt descending
  allItems.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  // Deduplicate
  const dedupedItems = deduplicateItems(allItems);

  // Log summary
  const totalSources = Object.values(sources).filter(v => v > 0).length;
  if (import.meta.env.DEV) console.log(`[Vault] Loaded ${dedupedItems.length} items from ${totalSources} sources:`, sources);

  // Cache the full result
  setCachedVault(userId, dedupedItems);

  // Apply filters and return
  return applyFilters(dedupedItems, { limit, daysBack, sourceTypes });
}

/**
 * Apply filters to items array
 */
function applyFilters(items, { limit, daysBack, sourceTypes }) {
  let filtered = items;

  // Filter by source types
  if (sourceTypes && sourceTypes.length > 0) {
    filtered = filtered.filter(item => sourceTypes.includes(item.sourceType));
  }

  // Filter by days back (already applied at query level, but double-check)
  if (daysBack) {
    const cutoff = new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000);
    filtered = filtered.filter(item => new Date(item.createdAt) >= cutoff);
  }

  // Apply limit
  if (limit && limit > 0) {
    filtered = filtered.slice(0, limit);
  }

  return filtered;
}

/**
 * Get items count by source
 */
export async function getItemCountsBySource(userId) {
  const items = await getAllItems(userId);
  
  const counts = {};
  for (const item of items) {
    const source = item.originalSourceTable;
    counts[source] = (counts[source] || 0) + 1;
  }
  
  return {
    total: items.length,
    bySource: counts,
  };
}

/**
 * Get recent items (convenience method)
 */
export async function getRecentItems(userId, limit = 12) {
  return getAllItems(userId, { limit });
}

/**
 * Get items from last N days (convenience method)
 */
export async function getItemsFromLastDays(userId, days = 7) {
  return getAllItems(userId, { daysBack: days });
}

/**
 * Search items by text
 */
export async function searchItems(userId, query, options = {}) {
  const items = await getAllItems(userId, options);
  const queryLower = query.toLowerCase();
  
  return items.filter(item => {
    const searchText = [
      item.title,
      item.description,
      item.textContent,
      ...(item.tags || []),
      ...(item.microTopics || []),
    ].join(' ').toLowerCase();
    
    return searchText.includes(queryLower);
  });
}

/**
 * Get items by channel
 */
export async function getItemsByChannel(userId, channel) {
  const items = await getAllItems(userId);
  return items.filter(item => item.channel === channel);
}

/**
 * Expose debug helper
 */
if (typeof window !== 'undefined') {
  window.vaultService = {
    getAllItems,
    getItemCountsBySource,
    clearVaultCache,
    searchItems,
  };
  if (import.meta.env.DEV) {
    console.log('[Vault] Debug commands available: window.vaultService.getAllItems(userId)');
  }
}

// Default export for convenience
export default {
  getAllItems,
  getRecentItems,
  getItemsFromLastDays,
  getItemCountsBySource,
  searchItems,
  getItemsByChannel,
  clearVaultCache,
};

